import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import markerSDK from '@marker.io/browser';

interface MarkerContextProps {
  show: () => void;
  hide: () => void;
  capture: (mode?: 'fullscreen' | 'advanced') => void;
  cancelCapture: () => void;
  unload: () => void;
  isExtensionInstalled: () => Promise<boolean>;
  setReporter: (data: { email: string; fullName: string }) => Promise<boolean>;
  setCustomData: (data: any) => void;
}

export const MarkerContext = createContext<MarkerContextProps | undefined>(
  undefined,
);

interface MarkerProviderProps {
  children: ReactNode;
  project: string;
}

/**
 * Pollyfill used by Marker.io/browser breaks fetch with EventSourcePolyfill
 */
export const MarkerProvider: React.FC<MarkerProviderProps> = ({
  children,
  project,
}) => {
  const [marker, setMarker] = useState<any>(null);

  useEffect(() => {
    const initMarker = async () => {
      const widget = await markerSDK.loadWidget({ project });
      setMarker(widget);
    };

    if (import.meta.env.VITE_APP_ENV !== 'local') {
      initMarker();
    }
  }, []);

  const contextValue: MarkerContextProps = {
    show: () => marker?.show(),
    hide: () => marker?.hide(),
    capture: mode => marker?.capture(mode),
    cancelCapture: () => marker?.cancelCapture(),
    unload: () => marker?.unload(),
    isExtensionInstalled: () => marker?.isExtensionInstalled(),
    setReporter: data => marker?.setReporter(data),
    setCustomData: data => marker?.setCustomData(data),
  };

  return (
    <MarkerContext.Provider value={contextValue}>
      {children}
    </MarkerContext.Provider>
  );
};

export const useMarker = () => {
  const context = useContext(MarkerContext);
  if (context === undefined) {
    throw new Error('useMarker must be used within a MarkerProvider');
  }
  return context;
};
